import { useEffect } from 'react';
import { formatDateTime } from '@/lib/media';
import type { ContactInquiry, MessageField } from '@/lib/contacts';

interface Props {
  inquiry: ContactInquiry | null;
  fields: MessageField[];
  onClose: () => void;
}

export default function ContactDetailDrawer({ inquiry, fields, onClose }: Props) {
  useEffect(() => {
    if (!inquiry) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [inquiry, onClose]);

  if (!inquiry) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-foreground-950/40 animate-[fade-in_0.2s_ease-out]"
        onClick={onClose}
      />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-background-200 bg-background-50 animate-[scale-in_0.2s_ease-out] sm:rounded-l-lg">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-mail-open-line" />
            </span>
            <h2 className="font-heading text-sm font-semibold">お問い合わせ詳細</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200"
            aria-label="閉じる"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          <dl className="space-y-3">
            <DetailRow label="受信日時" value={formatDateTime(inquiry.created_at)} />
            <DetailRow label="件名" value={inquiry.subject || '-'} />
            <DetailRow label="メールアドレス" value={inquiry.email || '-'} />
          </dl>

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-foreground-500">
              フォーム内容
            </p>
            {fields.length === 0 ? (
              <p className="text-sm text-foreground-400">内容がありません。</p>
            ) : (
              <div className="space-y-2">
                {fields.map((field) => (
                  <div
                    key={field.key}
                    className="rounded-md border border-background-200 bg-background-100/60 px-3 py-2"
                  >
                    <p className="text-[11px] font-medium uppercase tracking-wide text-foreground-500">
                      {field.key}
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-foreground-900">
                      {field.value}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-background-100 pb-3 last:border-0">
      <dt className="shrink-0 text-sm text-foreground-500">{label}</dt>
      <dd className="text-right text-sm text-foreground-900">{value}</dd>
    </div>
  );
}